import moment from 'moment';
import { SpendSourceDescriptor } from './AnalyticsUtils';

const DataSet = require('@antv/data-set');

const axisTextStyle = {
	fontSize: '12',
	fontFamily: 'Roboto',
	fill: 'rgba(0,0,0,0.85)',
};

// Fold per-source amounts into long-form { [xField], source, value } records so a single stacked-bar
// geom (color = source) renders one bar segment per spend source. Only `activeSources` are folded in.
const foldBySource = (rows: any[], xField: string, activeSources: SpendSourceDescriptor[]) => {
	const mapped = (rows || []).map((row) => {
		const next: any = { [xField]: row[xField] };
		activeSources.forEach((source) => {
			next[source.label] = parseFloat(row[source.key]) || 0;
		});
		return next;
	});
	const dataSet = new DataSet();
	const view = dataSet.createView().source(mapped);
	view.transform({
		type: 'fold',
		fields: activeSources.map((source) => source.label),
		key: 'source',
		value: 'value',
	});
	return view;
};

// Maps SpendCostsByField rows to the {fieldName, subTotal, percentageOfTotal} shape
// AnalyticsBreakdownChartSection's comparison view + CSV export expect. `sources` is the full set the
// tab totals over (all sources, or invoice-only for Suppliers) — NOT just the currently-active ones,
// so a comparison period that has spend in a source absent from the current period isn't undercounted.
// `percentageOfTotal` is each row's share of the grand total (exportBreakdownCsv reads it). Sorted
// ascending to match the breakdown ordering.
export const toBreakdownTotalsRows = (fieldRows: any[], sources: SpendSourceDescriptor[]) => {
	const rows = (fieldRows || []).filter(Boolean).map((row) => ({
		...row,
		subTotal: sources.reduce((sum, source) => sum + (parseFloat(row[source.key]) || 0), 0),
	}));
	const grandTotal = rows.reduce((sum, row) => sum + row.subTotal, 0);
	return rows
		.map((row) => ({
			...row,
			percentageOfTotal: grandTotal > 0 ? (row.subTotal / grandTotal) * 100 : 0,
		}))
		.sort((rowA, rowB) => rowA.subTotal - rowB.subTotal);
};

// Monthly stacked-bar config: spend split by source across months. `monthlyData` rows are
// SpendCostsMonthly objects (string amounts).
export const getSpendBySourceMonthlyChartConfig = (
	currency: any,
	activeSources: SpendSourceDescriptor[]
) => ({
	xAxisName: 'yearMonthKey',
	yAxisName: 'value',
	position: 'yearMonthKey*value',
	color: 'source',
	cols: {
		yearMonthKey: { alias: 'Month' },
		value: { alias: 'Spend' },
	},
	transformer: (monthlyData: any[]) => foldBySource(monthlyData, 'yearMonthKey', activeSources),
	yAxisLabel: {
		formatter: (val) => (val > 0 ? currency.symbolFormat(val, 0) : currency.symbolFormat(0, 0)),
		textStyle: axisTextStyle,
	},
	xAxisLabel: {
		formatter: (val) => moment(val, 'YYYY-MM').format('MMM YYYY'),
		textStyle: axisTextStyle,
	},
});
